/**
 * The moment someone asks for something their plan has not got.
 *
 * Every wall in the product used to end badly. Two new users made their free AI
 * video, came back minutes later for a second, met an untranslated "Monthly
 * Limit Reached" box with no price on it, and left without opening the pricing
 * page. The minutes wall was worse: a red English sentence and a small link, in
 * a product where most people read Spanish. Stripe shows three checkouts started
 * in thirty days against 4,176 people who ever made a project — almost nobody
 * was reaching the price at all.
 *
 * So: their language, what Pro gives, what it costs, and the first button goes
 * straight to checkout. "See all plans" is second, and where a free way forward
 * exists (the first N minutes of a long video) it sits beside the paid one,
 * because for someone with minutes left that is the better answer.
 *
 * Every other thing the free plan has not got opens this too (`reason`): the
 * watermark on a finished video, animating, editing a picture, a voice redo, a
 * longer video. Each had a window of its own with no price and no discount that
 * led to the pricing page and tracked nothing; in a week 22 people pressed
 * "remove watermark" on their video, as many as pressed the out-of-videos button
 * (2026-09-29).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Crown, Loader2, Sparkles, Clock, Scissors, BadgeCheck, Gift } from 'lucide-react';
import { activeSeason, seasonPaidOffer } from '../../growth/season';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '../../api/client.js';
import { track } from '../../analytics/track';
import { useLocale } from '../../i18n/LocaleContext';
import { useLocalPricing } from '../../hooks/useLocalPricing';
import { halfAmount, wholeNumber } from '../../utils/money';
import { PRICING_PLANS } from '../../config/pricing';
import { rememberCheckout } from '../../utils/checkoutTrip';
import { PAGES } from '../../i18n/routes';

// What one bundle of Pro gives of each thing a video can ask for (per video)
const PER_VIDEO = PRICING_PLANS.proPackage.perPackage.aiOps;
const FEATURES = {
  animate: PER_VIDEO.animations,
  edit: PER_VIDEO.imageEdits,
  generate: PER_VIDEO.imageRedraws,
  voice: PER_VIDEO.voiceRedos,
  // The free plan draws 3 pictures a scene like Pro since 2026-10-14: the quality offer names no number
  quality: null,
};
// Where the pricing page keeps the half price this offer just showed
export const OFFER_PRICING = '/pricing?offer=1';
// In the reader's own language: /pricing?offer=1 redirected a Spanish reader to
// /es/precios?offer=1, two page loads on a phone before anything showed
export const offerPricingPath = (locale) => `${PAGES.pricing?.[locale] || '/pricing'}?offer=1`;
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { countHours } from '../../i18n/countHours';

// A button whose words may be longer than a phone is wide: it wraps instead of widening the offer
const WRAP = 'h-auto min-h-9 w-full whitespace-normal py-2 leading-snug';

export default function UpgradeOffer({
  open,
  onClose,
  // What they ran out of or asked for: 'videos' (AI Agent), 'minutes' (processing
  // time), 'watermark', 'longer' (a video over the free plan's length), 'quality' (the
  // preview's Pro redraw of a free picture), or a per-video feature: 'animate', 'edit',
  // 'generate', 'voice'
  reason = 'videos',
  tool,
  used,
  limit,
  // The plan's own number and the unspent bonus videos behind `limit`
  planLimit,
  bonus,
  renewsOn,
  minutesLeft,
  minutesNeeded,
  // A free way forward, when there is one: {label, onClick}
  alternative = null,
  source = 'generate',
}) {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [starting, setStarting] = useState(false);
  // Someone who already pays is not sold the plan again: checkout would start a
  // second subscription beside theirs (it did, 2026-09-29). Their way to more is a
  // bigger plan, changed on the pricing page through the billing portal
  const { subscription } = useSubscriptionStore() || {};
  const subscriber = Boolean(subscription?.id) && ['active', 'trialing'].includes(subscription?.status);
  // What a bundle costs where this visitor is, and the id to check out with:
  // the ids differ per country, so a hardcoded one would charge the wrong money
  const pricing = useLocalPricing();
  const monthly = pricing.monthly;
  // The amount written the way their country writes it; the words around it
  // come from the dictionary, because a Spanish reader in the US wants
  // "US$15 al mes"
  // "US$15 al mes", "19 US$ par mois", "R$59 por mês" (hooks/useLocalPricing: the page's language's style)
  const charged = pricing.label(monthly);
  // Egypt is charged in dollars; the pounds it comes to go beside them ("US$11.49 (≈ 575 ج.م)")
  const withLocal = (label) => (monthly.local_amount != null
    ? `${label} (${t('pricing.localApprox', { amount: wholeNumber(monthly.local_amount) })})`
    : label);
  const price = withLocal(charged);
  const videos = pricing.ai_agent_videos ?? 10;
  const hours = pricing.hours ?? 5;
  const coupon = pricing.coupon;
  const halfPriceShown = coupon?.percent_off === 50 && coupon?.applies_to === 'first_month';
  // What they pay today, said first: a Mexican payer saw MX$159 and paid MX$79.50,
  // the half price only in a small line under the button (2026-09-30). Not for Egypt,
  // whose dollar price halves between cents and shows pounds beside it
  const firstAmount = halfPriceShown && !subscriber && monthly.local_amount == null
    ? halfAmount(pricing.currency, monthly.amount) : null;
  const first = firstAmount != null ? pricing.exact(firstAmount) : null;
  // `coupon` on the shown event too: whether the discount was on screen is half
  // of why someone did or did not press the button
  const properties = {
    reason, tool, source, used, limit, plan_limit: planLimit, bonus, subscriber,
    currency: pricing.currency, country: pricing.country,
    coupon: halfPriceShown ? coupon.id : null,
    // Whether the button led with today's payment (the first month's half price)
    first_month: firstAmount,
  };

  // Report what was actually on screen. The pricing query answers with dollars
  // while it is in flight, so firing the moment the dialog opens would file a
  // Mexican visitor's wall under usd — the report is held until their country's
  // price arrives, and sent with the fallback if it never does.
  const reported = useRef(false);
  useEffect(() => {
    if (!open) {
      reported.current = false;
      return undefined;
    }
    if (reported.current) return undefined;
    if (pricing.country) {
      reported.current = true;
      track('upgrade_offer_shown', properties);
      return undefined;
    }
    const late = setTimeout(() => {
      if (reported.current) return;
      reported.current = true;
      track('upgrade_offer_shown', { ...properties, pricing: 'fallback' });
    }, 1500);
    return () => clearTimeout(late);
  }, [open, pricing.country]); // eslint-disable-line react-hooks/exhaustive-deps

  // Straight to checkout: the plan has one price, so asking them to pick it
  // again on another page is a step that only loses people
  const goToCheckout = async () => {
    if (subscriber) {
      track('upgrade_offer_add_to_plan', properties);
      onClose();
      navigate('/pricing');
      return;
    }
    track('upgrade_offer_checkout', { ...properties, price: withLocal(pricing.apiLabel(monthly)) });
    setStarting(true);
    // For the way back from Stripe: what was paid, and the page they were on
    // (someone who paid to take a watermark off wants that video, not a blank one)
    rememberCheckout({
      value: monthly.amount, currency: pricing.currency, reason,
      returnTo: `${location.pathname}${location.search}`,
    });
    try {
      // from a wall: the API decides the coupon, the browser only says where
      const { url } = await api.createCheckoutSession(monthly.price_id, 1, { fromLimit: true, reason });
      window.location.href = url;
    } catch (error) {
      console.error('Checkout from the upgrade offer failed:', error);
      setStarting(false);
      navigate(offerPricingPath(locale));
    }
  };

  // The pricing page keeps the half price they were just shown: it used to charge
  // the full one, and more people took this button than the one above it
  const seePlans = () => {
    track('upgrade_offer_pricing', properties);
    onClose();
    navigate(offerPricingPath(locale));
  };

  // Someone out of videos who wants one or two more, not a subscription: 3 videos paid once
  // (services/packs.py). 2026-10-18: 248 of 270 checkouts were left with the billing form
  // untouched, most opened at this wall
  const pack = reason === 'videos' ? pricing.pack : null;
  const [packStarting, setPackStarting] = useState(false);
  const buyPack = async () => {
    track('pack_checkout', { ...properties, pack_price: pack.label });
    setPackStarting(true);
    rememberCheckout({ value: pack.amount, currency: pack.currency, reason: 'pack', returnTo: `${location.pathname}${location.search}` });
    try {
      const { url } = await api.createPackCheckout(reason);
      window.location.href = url;
    } catch (error) {
      console.error('Pack checkout failed:', error);
      setPackStarting(false);
    }
  };

  const takeAlternative = () => {
    track('upgrade_offer_alternative', properties);
    onClose();
    alternative.onClick();
  };

  // Scissors for "cut a shorter part"; a caller with another way forward brings its own
  const AlternativeIcon = alternative?.icon || Scissors;
  const feature = FEATURES[reason];
  // Paying this month adds bonus videos (October 2026: 5; services/paid_season.py); a subscriber already has them
  const paidSeasonVideos = subscriber ? null : seasonPaidOffer(activeSeason());
  const title = reason === 'minutes' ? t('upgrade.minutesTitle')
    // The free plan's video is one per account (2026-10-26): "this month" is a plan's words, not its
    : reason === 'videos' ? t(subscriber ? 'upgrade.videosTitle' : 'upgrade.freeVideosTitle')
    : t(`upgrade.${reason}Title`);
  const body = subscriber
    ? t(reason === 'minutes' ? 'upgrade.subscriberMinutesBody'
      : reason === 'videos' ? 'upgrade.subscriberBody'
      : 'upgrade.subscriberFeatureBody')
    : reason === 'minutes'
    ? t('upgrade.minutesBody', { needed: Math.round(minutesNeeded ?? 0), left: Math.round(minutesLeft ?? 0) })
    : reason !== 'videos'
    ? t(`upgrade.${reason}Body`, { n: feature })
    : renewsOn
      ? t('upgrade.bodyRenews', { date: renewsOn })
      : t('upgrade.body');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-offer">
        {/* min-w-0 and buttons that wrap: Egypt's button ("US$11.49 (≈ 575 ج.م) في الشهر") is wider
            than a 360 px phone's window, and kept on one line it pushed the whole offer 21 px past
            the window's edge, cut off on the left in Arabic (a replay, 2026-10-03) */}
        <div className="min-w-0 space-y-4 py-2 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
            <Crown className="h-6 w-6" />
          </div>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{body}</DialogDescription>

          <div className="rounded-lg border bg-muted/40 p-3 text-start text-sm">
            <p className="font-medium text-foreground">{first ? t('upgrade.planNameIntro', { first, price }) : t(subscriber ? 'upgrade.bundleName' : 'upgrade.planName', { price })}</p>
            <p className="mt-1.5 flex items-center gap-2 text-muted-foreground">
              <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
              {t('upgrade.planHours', { n: countHours(hours, locale) })}
            </p>
            <p className="mt-1 flex items-center gap-2 text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
              {t('upgrade.planVideos', { n: videos })}
            </p>
            <p className="mt-1 flex items-center gap-2 text-muted-foreground">
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
              {t('upgrade.planNoWatermark')}
            </p>
            {!subscriber && (
              <>
                <p className="mt-1 flex items-center gap-2 text-muted-foreground" data-testid="upgrade-offer-longer">
                  <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
                  {t('upgrade.planLonger')}
                </p>
                <p className="mt-1 flex items-center gap-2 text-muted-foreground" data-testid="upgrade-offer-animate">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                  {t('upgrade.planAnimate')}
                </p>
              </>
            )}
            {paidSeasonVideos && (
              <p className="mt-1 flex items-center gap-2 font-medium text-orange-600 dark:text-orange-300" data-testid="upgrade-offer-season">
                <Gift className="h-3.5 w-3.5 shrink-0" />
                {t('upgrade.planSeason', { n: paidSeasonVideos })}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {/* `alternative.primary`: the free way forward first, Pro second (a Pro wall met by
                someone who has made nothing yet: ux-guy, 2026-10-04, 5 checkouts from walls, none paid) */}
            {alternative?.primary && (
              <Button onClick={takeAlternative} className={WRAP} data-testid="upgrade-offer-alternative">
                <AlternativeIcon className="me-2 h-4 w-4" />
                {alternative.label}
              </Button>
            )}
            <Button onClick={goToCheckout} disabled={starting} variant={alternative?.primary ? 'outline' : 'default'} className={WRAP} data-testid="upgrade-offer-checkout">
              {starting ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Crown className="me-2 h-4 w-4" />}
              {starting ? t('upgrade.ctaLoading') : subscriber ? t('upgrade.addToPlan') : first ? t('upgrade.ctaIntro', { first }) : t('upgrade.cta', { price })}
            </Button>
            {halfPriceShown && !subscriber && (
              <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400" data-testid="upgrade-offer-coupon">
                {first ? t('upgrade.thenPrice', { price }) : t('upgrade.coupon')}
              </p>
            )}
            {pack && (
              <Button variant="outline" onClick={buyPack} disabled={packStarting} className={WRAP} data-testid="upgrade-offer-pack">
                {packStarting ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
                {packStarting ? t('upgrade.ctaLoading') : t('upgrade.pack', { n: pack.videos, price: pack.local_amount != null ? `${pricing.label(pack)} (${t('pricing.localApprox', { amount: wholeNumber(pack.local_amount) })})` : pricing.label(pack) })}
              </Button>
            )}
            {alternative && !alternative.primary && (
              <Button variant="outline" onClick={takeAlternative} className={WRAP} data-testid="upgrade-offer-alternative">
                <AlternativeIcon className="me-2 h-4 w-4" />
                {alternative.label}
              </Button>
            )}
            {!subscriber && (
              <Button variant="ghost" onClick={seePlans} className={WRAP}>
                {t('upgrade.secondary')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
